import { ReactNode, useCallback, useState } from 'react'

import { cn } from '@/utils/cn'

import { TabsBar, TabTitle, TabTitleIcon } from './styles'

export interface TabProps {
	content: ReactNode
	icons?: ReactNode
	id: string
	isDevMode?: boolean
	title: string
}

interface TabsProps {
	className?: string
	hasNewTable?: boolean,
	onChange?: (tab: TabProps) => void,
	selectedTabId?: string,
	tabs: TabProps[]
}

export const Tabs = ({
	tabs,
	onChange,
	selectedTabId,
	hasNewTable,
}: TabsProps) => {
	const [selectedTab, setSelectedTab] = useState(0)

	const handleChange = useCallback(
		(index: number) => {
			setSelectedTab(index)

			if (onChange) {
				onChange(tabs[index])
			}
		},
		[onChange, tabs],
	)

	const isSelected = useCallback(
		(index: number, tab: TabProps) =>
			(selectedTabId && selectedTabId === tab.id) ||
			(selectedTabId === undefined && index == selectedTab),
		[selectedTabId, selectedTab],
	)

	const tabsContainerStyles = cn('flex', 'flex-col', 'flex-grow', 'min-h-0', {
		'overflow-auto': !hasNewTable,
		'overflow-hidden': hasNewTable,
	})

	const tabsContentStyles = cn('flex-grow', 'min-h-0', 'flex', 'flex-col', {
		'overflow-auto content-start': !hasNewTable,
		'overflow-hidden': hasNewTable,
	})

	return (
		<div className={tabsContainerStyles} data-testid="tabs-container">
			<TabsBar role="tablist" data-testid="tabs-bar">
				{tabs.map((tab, i) => {
					const selected = isSelected(i, tab)

					return (
						<TabTitle
							role="tab"
							key={tab.title}
							onClick={() => handleChange(i)}
							$isSelected={selected}
							aria-selected={selected}
							data-testid={`tab-${tab.id}`}
						>
							<span>{tab.title}</span>
							{tab.icons && <TabTitleIcon data-testid={`tab-icon-${tab.id}`}>{tab.icons}</TabTitleIcon>}
						</TabTitle>
					)
				})}
			</TabsBar>
			<div className={tabsContentStyles} role="tabpanel" data-testid="tab-content">
				{tabs.map((tab, i) => isSelected(i, tab) && tab.content)}
			</div>
		</div>
	)
}
